/**
 * Permisos UI — Gestión Despacho (clientes operativos).
 * Fuente preferida: GET /auth/capabilities → permissions.write + role.
 * Fallback: campos legacy en user (/auth/me).
 */

import { canManageWorkspaceTeam } from "@/lib/auxiliaryRoleGuide";

export const DESPACHO_READ_ONLY_BADGE = "Modo solo lectura";

export const DESPACHO_READ_ONLY_HELP =
  "Tu usuario tiene acceso de consulta. No puede crear, editar ni eliminar clientes.";

export const DESPACHO_WRITE_FORBIDDEN_MESSAGE =
  "No tienes permiso para modificar clientes operativos.";

export const DESPACHO_STAFF_FORBIDDEN_MESSAGE =
  "No tienes permiso para administrar usuarios internos del despacho.";

export const DESPACHO_STAFF_READ_ONLY_HELP =
  "Tu usuario tiene acceso de consulta. No puede crear, editar ni desactivar usuarios internos.";

export const DESPACHO_ASSIGNMENTS_FORBIDDEN_MESSAGE =
  "No tienes permiso para administrar asignaciones.";

/**
 * @param {{
 *   permissions?: { write?: boolean } | null,
 *   role?: string | null,
 *   isPlatformDesigner?: boolean,
 *   capabilitiesLoading?: boolean,
 *   user?: object | null | false,
 * }} ctx
 */
export function canWriteDespacho(ctx = {}) {
  const {
    permissions,
    role,
    isPlatformDesigner = false,
    capabilitiesLoading = false,
    user,
  } = ctx;

  if (isPlatformDesigner) return true;

  if (typeof permissions?.write === "boolean") {
    return permissions.write;
  }

  const userWrite = _canWriteFromUser(user);
  if (userWrite !== null) return userWrite;

  if (capabilitiesLoading) return false;

  const rbacRole = String(role || "").trim().toLowerCase();
  if (rbacRole === "platform_designer") return true;
  if (rbacRole === "consulta") return false;
  if (rbacRole === "admin_cliente" || rbacRole === "operador") return true;

  return false;
}

/** @param {Parameters<typeof canWriteDespacho>[0]} ctx */
export function isDespachoReadOnly(ctx = {}) {
  return !canWriteDespacho(ctx);
}

/**
 * Columnas visibles en tabla de clientes operativos.
 * @param {boolean} canWrite
 */
export function despachoClientsTableIncludesActions(canWrite) {
  return Boolean(canWrite);
}

/**
 * Administra usuarios internos (manage_users / titular).
 * @param {Parameters<typeof canWriteDespacho>[0]} ctx
 */
export function canManageDespachoStaff(ctx = {}) {
  const {
    permissions,
    role,
    isPlatformDesigner = false,
    capabilitiesLoading = false,
    user,
  } = ctx;

  if (isPlatformDesigner) return true;

  if (canManageWorkspaceTeam(user)) return true;

  if (typeof permissions?.manage_users === "boolean") {
    return permissions.manage_users;
  }

  if (user?.is_admin_owner) return true;
  if (user?.is_buyer_tenant && String(user.role || "") === "buyer_admin") return true;

  if (capabilitiesLoading) return false;

  const rbacRole = String(role || "").trim().toLowerCase();
  if (rbacRole === "admin_cliente") return true;

  return false;
}

export function isDespachoStaffReadOnly(ctx = {}) {
  return !canManageDespachoStaff(ctx);
}

/** Administra asignaciones auxiliar ↔ cliente (manage_users / titular). */
export function canManageDespachoAssignments(ctx = {}) {
  return canManageDespachoStaff(ctx);
}

/**
 * @param {object | null | false | undefined} user
 * @returns {boolean | null} null = sin señal suficiente
 */
function _canWriteFromUser(user) {
  if (!user || user === false) return null;

  const mongoRole = String(user.role || "").trim().toLowerCase();
  const bntRole = String(user.bnt_org_role || "").trim().toLowerCase();

  if (mongoRole === "platform_designer" || user.is_designer_backdoor) return true;
  if (mongoRole === "consulta" || bntRole === "consulta") return false;
  if (user.is_admin_owner) return true;
  if (user.is_buyer_tenant && mongoRole === "buyer_admin") return true;
  if (bntRole === "admin_cliente" || bntRole === "operador") return true;
  if (mongoRole === "user" || mongoRole === "admin" || mongoRole === "revendedor") return true;

  return null;
}
